import {
  CreateVisitTypeDto,
  PatchVisitTypeDto,
  QueryFactory,
} from '@lmt/shared-types';
import { Error } from '@lmt/unicorn-shared/app/components/common/Error';
import { Field } from '@lmt/unicorn-shared/app/components/common/Field';
import { SubmitButton } from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/SubmitButton';
import { TimePicker } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/date-time/TimePicker';
import { Input } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/Input';
import { useVisitTypes } from '@lmt/unicorn-shared/app/features/dictionaries/selectors';
import { useAdvancedForm } from '@lmt/unicorn-shared/app/helpers/form/advanced-form';
import {
  maxRule,
  positiveRule,
  requiredRule,
} from '@lmt/unicorn-shared/app/helpers/form/react-hook-form-helper';
import {
  NS_DataBlockFields,
  useScopedTranslation,
} from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { useQueryClient } from '@tanstack/react-query';
import React, { FC, useMemo } from 'react';
import { Controller } from 'react-hook-form';
import { FormComponentProps } from '@lmt/unicorn-shared/app/components/global-forms/GlobalForms.types';

import globalFormStyles from '@lmt/unicorn-shared/app/components/global-forms/Content.module.scss';
import { useTranslation } from 'react-i18next';
import { useEventCallback } from '@mui/material';

type VisitTypeData = Omit<CreateVisitTypeDto, 'defaultDurationInSeconds'> & {
  defaultDurationInHours: number; // for convenience, we set duration in hours
};

const defaultVisitTypeData: VisitTypeData = {
  value: '',
  specificStartTimeInSecondsSinceMidnight: 32400, // 9:00
  defaultDurationInHours: 1,
};

type CreateOrEditVisitTypeFormProps = FormComponentProps & {
  id?: number;
};

export const CreateOrEditVisitType: FC<CreateOrEditVisitTypeFormProps> = (
  props,
) => {
  const i18next = useScopedTranslation('dictionaries', NS_DataBlockFields);
  const i18nextDataBlocks = useTranslation(NS_DataBlockFields);
  const { formType, formId, tryCompleteForm } = props;
  const queryClient = useQueryClient();

  const visitTypes = useVisitTypes();

  const visitTypeToEdit = useMemo(() => {
    return props.id ? visitTypes.find((x) => x.id === props.id) : undefined;
  }, [visitTypes, props.id]);

  const visitTypeToEditFormData = useMemo(() => {
    return visitTypeToEdit
      ? {
          ...visitTypeToEdit,
          value: i18nextDataBlocks.t(visitTypeToEdit.value as any),
          defaultDurationInHours:
            visitTypeToEdit.defaultDurationInSeconds / 3600,
        }
      : undefined;
  }, [visitTypeToEdit]);

  const form = useAdvancedForm<VisitTypeData>(
    useEventCallback(async (data) => {
      await tryCompleteForm(
        formType,
        formId,
        async () => {
          let createdVisitType = null;
          const dto = {
            ...data,
            defaultDurationInSeconds:
              // round the duration in hours to two decimal places and convert to seconds
              Math.round(data.defaultDurationInHours * 100) * 36,
          };
          if (visitTypeToEdit?.isDefault) {
            dto.value = undefined!;
          }
          if (props.id) {
            await QueryFactory.VisitTypeClient.patch(props.id, dto);
          } else {
            createdVisitType = await QueryFactory.VisitTypeClient.create(dto);
          }
          await queryClient.invalidateQueries({
            queryKey: QueryFactory.VisitTypeQuery.getQueryKey(),
          });
          if (createdVisitType) {
            return createdVisitType;
          }
        },
        i18next.t('form.loading'),
      );
    }),
    visitTypeToEditFormData,
    {
      shouldFocusError: true,
      criteriaMode: 'all',
      defaultValues: props.id ? undefined : defaultVisitTypeData,
    },
  );

  return (
    <form
      onSubmit={form.handleSubmitDefault}
      className={globalFormStyles.autoSizedContainer}
      data-test-id="editDictionaryItemPopup"
    >
      <div className={globalFormStyles.dataContainer}>
        <div className={globalFormStyles.fieldsContainer}>
          <Field
            title={i18next.t('visit_types.columns.value')}
            required={true}
            className={globalFormStyles.firstField}
          >
            <Input
              disabled={visitTypeToEdit?.isDefault}
              {...form.register('value', {
                setValueAs: (x: string) => x.trim(),
                ...requiredRule(),
              })}
              errorText={form.formState.errors.value?.message}
              autoFocus={true}
            />
          </Field>
          <div className={globalFormStyles.row}>
            <Field
              title={i18next.t('visit_types.columns.start_time')}
              className={globalFormStyles.rowField}
            >
              <Controller
                control={form.control}
                name={'specificStartTimeInSecondsSinceMidnight'}
                render={({ field: { onChange, value } }) => (
                  <TimePicker
                    timeInMills={value ? value * 1000 : value}
                    onTimeChanged={(value) =>
                      onChange(value ? value / 1000 : value)
                    }
                    timeEntriesIntervalInMinutes={30}
                  />
                )}
              />
            </Field>
            <Field
              title={i18next.t('visit_types.columns.duration')}
              required={true}
              className={globalFormStyles.fieldFlex}
            >
              <Input
                {...form.register('defaultDurationInHours', {
                  ...requiredRule(),
                  ...positiveRule(),
                  ...maxRule(100),
                  valueAsNumber: true,
                })}
                errorText={
                  form.formState.errors.defaultDurationInHours?.message
                }
              />
            </Field>
          </div>
        </div>
      </div>
      <div className={globalFormStyles.submitContainer}>
        <Error error={form.overallError} />
        <SubmitButton
          submitting={form.formState.isSubmitting}
          className={globalFormStyles.submit}
          title={
            props.id
              ? i18next.t('form.edit_button')
              : i18next.t('form.create_button')
          }
        />
      </div>
    </form>
  );
};
